/* eslint-disable react/prop-types */
import * as React from 'react'
import PropTypes from 'prop-types'
import AuthService from 'services/auth'
import { SSO_RETURN_URL_KEY } from 'constants/sso'

const AuthContext = React.createContext(undefined)

export default function AuthProvider({ auth, children }) {
  const [user, setUser] = React.useState()
  // When clicking logout we do not want the `?from=...` part
  // @see https://gitlab.com/eleo-rh/training/-/issues/8669
  const [bypassFrom, setBypassFrom] = React.useState(false)

  const logout = React.useCallback(() => {
    auth.logout()
  }, [auth])

  const login = React.useCallback(token => {
    loginFromToken(token)
    setUser(auth.getUser())
  }, [auth, loginFromToken])

  /**
   * SSO and Old Portal app come to this app with their own token
   * @see containers/Sso
   */
  const loginFromToken = React.useCallback(token => {
    auth.login(token)
    setUser(auth.getUser())
    setBypassFrom(false)
  }, [auth])

  const refreshToken = React.useCallback(t => {
    auth.login(t)
    // setUser(auth.getUser())
  }, [auth])

  // First render
  React.useEffect(() => {
    const hasValidToken = auth.validToken()

    if (hasValidToken) {
      setUser(auth.getUser())
      return () => {}
    }

    if (typeof window !== 'undefined') {
      const { pathname, search, hash } = window.location
      const lowerPath = pathname?.toLowerCase?.() || ''
      const isAuthRoute = lowerPath.includes('/login') || lowerPath.includes('/reset-password')
      const isSsoRoute = pathname === '/sso'

      if (!isAuthRoute && !isSsoRoute) {
        // Persist the original location before we wipe credentials here.
        // AuthProvider sits very early in the tree (index.js) so it is the first
        // place that detects "no valid token" for both SSO and classic auth flows.
        // Stashing the route here guarantees every entry point records it once.
        const intendedUrl = `${pathname}${search}${hash}`
        if (intendedUrl) {
          window.sessionStorage.setItem(SSO_RETURN_URL_KEY, intendedUrl)
        }
      }
    }

    auth.logout()

    return () => {}
  }, [auth])

  const value = React.useMemo(() => ({
    auth,
    bypassFrom,
    login,
    logout,
    loginFromToken,
    refreshToken,
    user,
  }), [auth, bypassFrom, login, logout, loginFromToken, refreshToken, user])

  return <AuthContext.Provider value={value}>{ children }</AuthContext.Provider>
}

AuthProvider.propTypes = {
  auth: PropTypes.instanceOf(AuthService),
  children: PropTypes.node,
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('AuthContext must be used within a Provider')
  }
  return context
}
